/**
 * Where a value comes from (step 1.1 of docs/plans/setup-walk.md): the walk
 * follows a field or a formula tree to its sources, as a person filling a new
 * entry would meet them. A value is typed by the person, read from a row the
 * entry picks, worked out by a formula, or never given at all.
 *
 * `calc` and the other operators need every input; `coalesce` and `if` take
 * the best branch, since a person reaches the value through either. A field
 * read twice is followed once, so a cycle stops.
 *
 * Pure: the phone bundles it.
 */
import type { SetupDraftFormulaNode } from '../compose-author';
import type { StudioDraft, StudioRecord } from '../setup-draft';

/** One list field a value reads through a pick: `via` on the record picks a row of `list`, whose `field` it reads. */
export type ListRead = { readonly list: string; readonly field: string; readonly via: string };

export type Reach = {
  /** Why no value can ever come, one sentence each; empty when one can. */
  readonly never: readonly string[];
  /** List fields the value comes from with no way to type over them. */
  readonly fromList: readonly ListRead[];
  /** List fields a default starts from: the person may type over them. */
  readonly startsFrom: readonly ListRead[];
  /** Whether it waits on a field the person may leave empty. */
  readonly optional: boolean;
};

export const SURE: Reach = { never: [], fromList: [], startsFrom: [], optional: false };

const never = (why: string): Reach => ({ ...SURE, never: [why] });

/** Every input is needed: the worst of them. */
export const allOf = (reaches: readonly Reach[]): Reach => ({
  never: reaches.flatMap((one) => one.never),
  fromList: reaches.flatMap((one) => one.fromList),
  startsFrom: reaches.flatMap((one) => one.startsFrom),
  optional: reaches.some((one) => one.optional),
});

/** Any branch will do: the best of them. A branch that needs no list frees the value from the list. */
export const bestOf = (reaches: readonly Reach[]): Reach => {
  const viable = reaches.filter((one) => one.never.length === 0);
  if (viable.length === 0) return allOf(reaches);
  const listFree = viable.some((one) => one.fromList.length === 0);
  return {
    never: [],
    fromList: listFree ? [] : viable.flatMap((one) => one.fromList),
    startsFrom: viable.flatMap((one) => one.startsFrom),
    optional: viable.every((one) => one.optional),
  };
};

type Walk = { readonly draft: Pick<StudioDraft, 'records'>; readonly record: StudioRecord; readonly seen: Set<string> };

const recordOf = (draft: Pick<StudioDraft, 'records'>, key: string | undefined): StudioRecord | undefined => draft.records.find((one) => one.key === key);

/** A field of the record, as a new entry meets it. */
const fieldReach = (walk: Walk, key: string): Reach => {
  const { record } = walk;
  const field = record.fields.find((one) => one.key === key);
  if (field === undefined) return never(`${record.name} has no field "${key}"`);
  if (walk.seen.has(key)) return SURE;
  walk.seen.add(key);
  try {
    if (field.type === 'formula') return field.formula === undefined ? never(`${field.label} on ${record.name} has no formula`) : nodeReach(walk, field.formula.expr);
    // A total of other entries, or of lines, is nought before there are any: it always has a value.
    if (field.type === 'rollup' || field.type === 'lines' || field.type === 'series') return SURE;
    const start = field.startsWith === undefined ? null : nodeReach(walk, field.startsWith.expr);
    // A default hands its list reads over as starting points: the person may type across it.
    const startsFrom = start === null || start.never.length > 0 ? [] : [...start.fromList, ...start.startsFrom];
    const filled = field.required === true || (start !== null && start.never.length === 0 && !start.optional);
    return { never: [], fromList: [], startsFrom, optional: !filled };
  } finally {
    walk.seen.delete(key);
  }
};

/** A field of the row a pick holds. A worked-out field of the list is the list's own business: it has a value. */
const hopReach = (walk: Walk, via: string, fieldKey: string): Reach => {
  const pick = walk.record.fields.find((one) => one.key === via);
  if (pick === undefined || (pick.type !== 'reference' && pick.type !== 'multi_select')) return never(`${walk.record.name} has no pick "${via}" to read ${fieldKey} through`);
  const list = recordOf(walk.draft, pick.targetRecordKey);
  if (list === undefined) return never(`${pick.label} on ${walk.record.name} picks from nothing`);
  const target = list.fields.find((one) => one.key === fieldKey);
  if (target === undefined) return never(`${list.name} has no field "${fieldKey}"`);
  if (target.type === 'formula' || target.type === 'rollup') return { ...SURE, optional: pick.required !== true };
  return { never: [], fromList: [{ list: list.key, field: fieldKey, via }], startsFrom: [], optional: pick.required !== true };
};

/** A total over lines or over the rows a multi_select holds: nought with none, so it always has a value once its parts exist. */
const aggReach = (walk: Walk, lines: string, of: string | undefined): Reach => {
  const holder = walk.record.fields.find((one) => one.key === lines);
  if (holder === undefined) return never(`${walk.record.name} has no lines "${lines}"`);
  if (of === undefined) return SURE;
  if (holder.type === 'lines') return holder.lines?.fields.some((one) => one.key === of) === true ? SURE : never(`the lines "${holder.label}" hold no field "${of}"`);
  const list = recordOf(walk.draft, holder.targetRecordKey);
  return list?.fields.some((one) => one.key === of) === true ? SURE : never(`${list?.name ?? holder.label} has no field "${of}"`);
};

export const nodeReach = (walk: Walk, node: SetupDraftFormulaNode): Reach => {
  switch (node.kind) {
    case 'const':
    case 'today':
      return SURE;
    case 'field':
      return fieldReach(walk, node.fieldKey);
    case 'hop':
      return hopReach(walk, node.via, node.fieldKey);
    case 'agg':
      return aggReach(walk, node.lines, node.of);
    case 'calc':
      return allOf([nodeReach(walk, node.left), nodeReach(walk, node.right)]);
    case 'round':
      return nodeReach(walk, node.of);
    case 'days_between':
      return allOf([nodeReach(walk, node.from), nodeReach(walk, node.to)]);
    case 'concat':
      return allOf(node.parts.map((one) => nodeReach(walk, one)));
    case 'coalesce':
      return bestOf(node.of.map((one) => nodeReach(walk, one)));
    case 'date_add':
      return allOf([nodeReach(walk, node.date), nodeReach(walk, node.amount), nodeReach(walk, node.unit)]);
    case 'if': {
      // A test that reads nothing breaks the choice; past that, either branch gives the value.
      const test = allOf([nodeReach(walk, node.left), ...(node.right === undefined ? [] : [nodeReach(walk, node.right)])]);
      const branches = bestOf([nodeReach(walk, node.then), nodeReach(walk, node.otherwise)]);
      return test.never.length > 0 ? { ...branches, never: test.never } : branches;
    }
  }
};

/** Where a field of `record` gets its value, in a new entry. */
export const reachOfField = (draft: Pick<StudioDraft, 'records'>, record: StudioRecord, fieldKey: string): Reach =>
  fieldReach({ draft, record, seen: new Set() }, fieldKey);

/** Every node of a tree, the tree's own first. */
export const nodesOf = (node: SetupDraftFormulaNode): SetupDraftFormulaNode[] => {
  switch (node.kind) {
    case 'calc':
      return [node, ...nodesOf(node.left), ...nodesOf(node.right)];
    case 'round':
      return [node, ...nodesOf(node.of)];
    case 'days_between':
      return [node, ...nodesOf(node.from), ...nodesOf(node.to)];
    case 'concat':
      return [node, ...node.parts.flatMap(nodesOf)];
    case 'coalesce':
      return [node, ...node.of.flatMap(nodesOf)];
    case 'date_add':
      return [node, ...nodesOf(node.date), ...nodesOf(node.amount), ...nodesOf(node.unit)];
    case 'if':
      return [node, ...nodesOf(node.left), ...(node.right === undefined ? [] : nodesOf(node.right)), ...nodesOf(node.then), ...nodesOf(node.otherwise)];
    default:
      return [node];
  }
};
